// UnitFeesViewModal — read-only drill-down for a single unit's fees.
// Sprint 5c extraction. Receives the selected summary, the role flag, and
// callbacks for "Mark Paid" / "Waive" so the orchestrator stays the only
// thing wiring those to store/api.
import React from 'react';
import { Button, Col, Modal, Row, Space, Table, Tag, Tooltip } from 'antd';
import { ApartmentOutlined, CheckOutlined, StopOutlined, UserOutlined } from '@ant-design/icons';
import { Fee } from '../../types';
import { realFeeId, statusDisplay, type UnitFeeSummary } from './feeRules';
import { formatDate } from '../../utils/date';
import { currency } from '../../utils/currency';   // Sprint 146 (R14-11)

export interface UnitFeesViewModalProps {
  open: boolean;
  summary: UnitFeeSummary | null;
  canManageFees: boolean;
  onCancel: () => void;
  onMarkPaid: (feeId: string) => void;
  onWaiveRequest: (realId: string) => void;
}

const StatusTag: React.FC<{ status: Fee['status'] }> = ({ status }) => {
  const { color, label } = statusDisplay(status);
  return <Tag color={color}>{label}</Tag>;
};

export const UnitFeesViewModal: React.FC<UnitFeesViewModalProps> = ({
  open, summary, canManageFees, onCancel, onMarkPaid, onWaiveRequest,
}) => (
  <Modal
    title={summary ? `Unit ${summary.unitNumber} - Fee Details` : 'Unit Details'}
    open={open}
    onCancel={onCancel}
    footer={[
      <Button key="close" onClick={onCancel}>Close</Button>,
    ]}
    width={800}
  >
    {summary && (
      <div>
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <Row gutter={16}>
            <Col span={8}>
              <div className="text-sm text-gray-500">Owner</div>
              <div className="font-medium">{summary.ownerName}</div>
            </Col>
            <Col span={8}>
              <div className="text-sm text-gray-500">Total Fees</div>
              <div className="font-medium">${summary.totalFees.toFixed(2)}</div>
            </Col>
            <Col span={8}>
              <div className="text-sm text-gray-500">Paid</div>
              <div className="font-medium text-green-600">${summary.paidFees.toFixed(2)}</div>
            </Col>
          </Row>
        </div>

        <Table
          columns={[
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              render: (type: string, record: Fee) => (
                <Space>
                  {record.isCondoWide ? (
                    <Tooltip title="Condo-wide Fee">
                      <ApartmentOutlined className="text-blue-500" />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Individual Fee">
                      <UserOutlined className="text-green-500" />
                    </Tooltip>
                  )}
                  <Tag color={record.isCondoWide ? 'blue' : 'green'}>
                    {type.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </Tag>
                </Space>
              ),
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              key: 'amount',
              render: (amount: number) => currency(amount),   // Sprint 146 (R14-11)
            },
            {
              title: 'Due Date',
              dataIndex: 'dueDate',
              key: 'dueDate',
              // Sprint 145 (R14-10) — this column had its OWN local fix for
              // the UTC-midnight bug (appending 'T00:00:00' to force a local
              // parse). It worked, and it was a fourth divergent treatment of
              // the same rule. `formatDate` now owns it.
              render: (date: Date | string) => formatDate(date),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (s: Fee['status']) => <StatusTag status={s} />,
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_: unknown, record: Fee) => (
                <Space>
                  {(record.status === 'PENDING' || record.status === 'OVERDUE') && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={() => onMarkPaid(record.id)}
                    >
                      Mark Paid
                    </Button>
                  )}
                  {canManageFees && (record.status === 'PENDING' || record.status === 'OVERDUE') && (
                    <Button
                      size="small"
                      icon={<StopOutlined />}
                      onClick={() => onWaiveRequest(realFeeId(record.id))}
                    >
                      Waive
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
          dataSource={summary.fees}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </div>
    )}
  </Modal>
);
